/**
 * PL-Line: size and colour filters list only what is in stock in that size or colour.
 *
 * Shopify matches a variant option filter (`filter.v.option.size=XS`) against every variant
 * with that value, sold out or not. Picking XS on /collections/all-men listed 107 products,
 * 67 of them with XS sold out (measured 2026-09-26). With the availability filter
 * (`filter.v.availability=1`) added, Shopify matches both on the SAME variant, so the list is
 * exactly the 40 with XS in stock. That same-variant rule was measured on the live store;
 * Shopify's docs do not state it outright.
 *
 * The rule: `filter.v.availability=1` is in the URL exactly when a variant option filter is.
 * It is not a shopper control (blocks/filters.liquid keeps it out of the filter UI), so it is
 * applied at the two places Horizon turns filter state into a URL:
 * - `createURLParameters`: ticking and unticking, clearing a group, sorting, and the prefetch
 *   on hover. One prefetch misses: unticking the last size prefetches a URL that still has the
 *   rule, because Horizon removes that value only after this runs. It goes unused, no harm.
 * - `updateFiltersByURL`: the remove pills, whose URL Shopify built (`url_to_remove`) still
 *   carries the rule after the last size or colour is removed, and "Clear all".
 * A link that arrives with the rule missing or wrong is corrected by blocks/filters.liquid.
 *
 * This wraps Horizon's methods instead of editing assets/facets.js, so a Horizon upgrade
 * cannot silently drop it, but it depends on those two methods: see CLAUDE.md, "Stock-aware
 * size and colour filters". If Horizon changes them, this steps aside and says so once in the
 * console. The list still shows only what is in stock, but every size or colour change then
 * reloads the whole page: the section's re-render brings back the arrival check in
 * blocks/filters.liquid, which adds the rule with a full page load.
 */
const STOCK_PARAM = 'filter.v.availability';
const OPTION_PREFIX = 'filter.v.option.';

let warned = false;
/**
 * @param {string} message
 * @param {unknown} [detail]
 */
function warnOnce(message, detail) {
  if (warned) return;
  warned = true;
  console.warn(
    `[pl-stock-aware-filters] ${message} Size and colour changes now reload the whole page (the arrival check in blocks/filters.liquid adds the stock rule). ` +
      'See CLAUDE.md, "Stock-aware size and colour filters".',
    detail ?? ''
  );
}

/**
 * @param {URLSearchParams} params
 * @returns {boolean}
 */
function hasOptionFilter(params) {
  for (const [key, value] of params) {
    if (key.startsWith(OPTION_PREFIX) && value !== '') return true;
  }
  return false;
}

/**
 * Adds the stock rule when a variant option filter is present, removes it otherwise.
 * @param {URLSearchParams} params
 * @returns {URLSearchParams}
 */
function applyStockRule(params) {
  const hasOption = hasOptionFilter(params);
  params.delete(STOCK_PARAM);
  if (hasOption) params.set(STOCK_PARAM, '1');
  return params;
}

/**
 * @param {URLSearchParams} params
 * @returns {boolean}
 */
function obeysStockRule(params) {
  return hasOptionFilter(params) ? params.getAll(STOCK_PARAM).join(',') === '1' : !params.has(STOCK_PARAM);
}

customElements
  .whenDefined('facets-form-component')
  .then(() => {
    const proto = customElements.get('facets-form-component')?.prototype;
    if (!proto || proto.plStockAware) return;

    const { createURLParameters, updateFiltersByURL } = proto;
    if (typeof createURLParameters !== 'function' || typeof updateFiltersByURL !== 'function') {
      warnOnce('facets-form-component no longer has createURLParameters and updateFiltersByURL.');
      return;
    }
    proto.plStockAware = true;

    /** @param {...any} args */
    proto.createURLParameters = function (...args) {
      const params = createURLParameters.apply(this, args);
      if (!(params instanceof URLSearchParams)) {
        warnOnce('createURLParameters no longer returns URLSearchParams.', params);
        return params;
      }
      return applyStockRule(params);
    };

    /**
     * @param {unknown} url
     * @param {...any} args
     */
    proto.updateFiltersByURL = function (url, ...args) {
      if (typeof url !== 'string' && !(url instanceof URL)) {
        warnOnce('updateFiltersByURL was called with something other than a URL.', url);
        return updateFiltersByURL.call(this, url, ...args);
      }
      const next = new URL(url, window.location.href);
      applyStockRule(next.searchParams);
      return updateFiltersByURL.call(this, `${next.pathname}${next.search}${next.hash}`, ...args);
    };
  })
  .catch((error) => warnOnce('could not be installed.', error));

// Check the outcome, not just the method names: Horizon dispatches these right after it
// pushes the new filter URL, so a URL without the rule here means a filter change went
// around the two wrapped methods.
for (const type of ['shopify:collection:update', 'shopify:search:update']) {
  document.addEventListener(type, () => {
    if (!obeysStockRule(new URLSearchParams(window.location.search))) {
      warnOnce(`A filter change produced "${window.location.search}" without the stock rule.`);
    }
  });
}
window.addEventListener('load', () => {
  if (!customElements.get('facets-form-component')) {
    warnOnce('facets-form-component was never defined (facets.js failed to load, or was renamed).');
  }
});
